import type { JSONSchema7 } from "json-schema";
import type {
  CreateMcpConnection,
  McpConnection,
  UpdateMcpConnection,
} from "@/schemas/mcp-connection.schema";
import type { RequestConfig } from "@/services/api";
import { api } from "@/services/api";

export interface McpConnectPayload {
  name?: string;
  url?: string;
  transport?: McpConnection["transport"];
  headers?: Record<string, string>;
  connection_id?: string;
  popup?: boolean;
}

export interface McpConnectTool {
  name: string;
  description?: string;
  inputSchema: JSONSchema7;
}

export interface McpConnectResult {
  connection: McpConnection;
  authorization_url: string | null;
  tools: McpConnectTool[];
}

export const mcpConnectionsApi = {
  async list(config?: RequestConfig) {
    const { data } = await api<McpConnection[]>("/mcp/connections", config);
    return data;
  },

  async create(payload: CreateMcpConnection, config?: RequestConfig) {
    const { data } = await api.post<McpConnection>("/mcp/connections", payload, config);
    return data;
  },

  async update(id: string, payload: UpdateMcpConnection) {
    const { data } = await api.patch<McpConnection>(`/mcp/connections/${id}`, payload);
    return data;
  },

  async remove(id: string) {
    await api.delete(`/mcp/connections/${id}`);
  },

  async test(id: string) {
    const { data } = await api.post<McpConnection>(`/mcp/connections/${id}/test`);
    return data;
  },

  async syncTools(id: string) {
    const { data } = await api.post<McpConnection>(`/mcp/connections/${id}/tools/sync`);
    return data;
  },

  async startOAuth(id: string, payload?: { popup?: boolean }) {
    const { data } = await api.post<{ authorization_url: string | null; connection: McpConnection }>(
      `/mcp/connections/${id}/oauth/start`,
      payload ?? {},
    );
    return data;
  },

  async revokeOAuth(id: string) {
    const { data } = await api.post<McpConnection>(`/mcp/connections/${id}/oauth/revoke`);
    return data;
  },

  async connect(payload: McpConnectPayload, config?: RequestConfig) {
    const { data } = await api.post<McpConnectResult>("/mcp/connections/connect", payload, config);
    return data;
  },
};
